import { useCallback, useMemo, useState } from 'react';
import { useDebounce } from 'react-use';
import { DEFAULT_INPUT_DELAY } from 'utility/config';
import { getOrderingParam, getSortValue } from 'utility/SortingHelpers';

export type CommonPaginationConfig = {
  page?: number;
  pageSize?: number;
  search?: string;
  ordering?: string;
  [key: string]: any;
};

export const usePaginationSettings = (
  defaultPaginationSettings: CommonPaginationConfig
) => {
  const [state, setPaginationSettings] = useState(defaultPaginationSettings);

  const { page = 1, pageSize = 10, search = '', ordering = '' } = state;

  const [debauncedSearch, setDebouncedSearch] = useState(search);

  useDebounce(() => setDebouncedSearch(search), DEFAULT_INPUT_DELAY, [search]);

  const handleChangePage = useCallback(
    (page: number) =>
      setPaginationSettings((prev) => ({
        ...prev,
        page,
      })),
    []
  );

  const handleChangeSearch = useCallback(
    (search: string) =>
      setPaginationSettings((prev) => ({
        ...prev,
        page: 1,
        search,
      })),
    []
  );

  const handleChangePageSize = useCallback((pageSize: number) => {
    setPaginationSettings((prev) => ({
      ...prev,
      page: 1,
      pageSize,
    }));
  }, []);

  const handleChangeOrdering = useCallback(
    (column: string, direction: 'asc' | 'desc') =>
      setPaginationSettings((prev) => ({
        ...prev,
        ordering: getOrderingParam({ column, direction }),
      })),
    []
  );

  const headData = useMemo(
    () => ({
      meta: {
        sort: getSortValue(ordering),
        onSort: handleChangeOrdering,
      },
    }),
    [ordering, handleChangeOrdering]
  );

  return {
    page,
    pageSize,
    search: debauncedSearch,
    actualSearch: search,
    ordering,
    handleChangePage,
    handleChangeSearch,
    handleChangePageSize,
    handleChangeOrdering,
    headData,
    paginationSettings: state,
    setPaginationSettings,
  };
};
